/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import { links } from '@/utils/link'
import type { CreateOpportunitiesRoutesOptions } from './routes'
import type { OpportunitiesRootViewModel } from './OpportunitiesRootViewModel'
import { Id } from '@/infra/id'

/**
 * Data returned by {@link createOpportunitiesViewLoader}.
 */
export interface OpportunitiesViewLoaderData {
  vm: OpportunitiesRootViewModel
}

/**
 * Creates a loader function for the opportunities view that redirects to the
 * jobs page when the user may not view any opportunities, and otherwise
 * provides the view model and asks it to view the route's opportunity.
 *
 * @param options
 */
export function createOpportunitiesViewLoader(
  options: CreateOpportunitiesRoutesOptions,
): LoaderFunction {
  return async ({ params, request }): Promise<OpportunitiesViewLoaderData> => {
    await options.waitForAuthResolution(request.signal)
    const internal = await options.opportunities.internal()
    const [canViewOps, canViewWorkspaceOps] = await Promise.all([
      internal.opportunityStore.probeCanViewOpportunities(),
      internal.workspacePostingOpportunityStore.canViewWorkspaceOpportunities(),
    ])
    if (!canViewOps && !canViewWorkspaceOps) {
      throw redirect(links.jobs())
    }

    const vm = internal.opportunitiesRootViewModel
    vm.requestOpportunity(
      params.opportunityId ? Id(params.opportunityId) : null,
    )
    return { vm }
  }
}
